import { AxiosRequestConfig, AxiosResponse } from 'axios';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CommonAPI, SportsApi } from '../../../api';
import { AvailableFiltersResponse, EventStartFilterResponse } from '../../../api/common.generated';
import { defaultEventStartFilters } from '../../../features/sports/utility/utility';
import { createLogger } from '../../logger';
import { fetchCategories, fetchPopularCategories } from '../../store/categories';
import { UseEventStartFiltersType } from './useEventStartFilters.types';
import { useEventStartFiltersConsts } from './useEventStartFilters.utils';

export const useEventStartFilters = ({ refreshCategoriesOnFilterChange }: UseEventStartFiltersType = {}) => {
	const logger = createLogger('useEventStartFilters');
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();
	const [availableFilters, setAvailableFilters] = useState<EventStartFilterResponse[] | undefined>(undefined);

	const getFilterValueFromQueryString = () => {
		const filterValue = queryString.parse(location.search)[useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM];

		if (filterValue instanceof Array) {
			return filterValue[0] || undefined;
		}

		return filterValue || undefined;
	};

	// We need to save this value twice because some components such as Breadcrumbs don't have the availableFilters so they cannot use currentFilterValue.
	// Instead, they will use the currentSearchParamsFilterValue which doesn't change when a filter is not currently available.
	const [currentFilterValue, setCurrentFilterValue] = useState<string | undefined>(getFilterValueFromQueryString());
	const [currentSearchParamsFilterValue, setCurrentSearchParamsFilterValue] = useState<string | undefined>(getFilterValueFromQueryString());

	const updateFilterSearchParam = (value: string | undefined) => {
		const searchParams = new URLSearchParams(location.search);

		if (value) {
			if (searchParams.get(useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM) === value) {
				// Avoid unnecessary history.replace()
				return;
			}
			searchParams.set(useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM, value);
		} else {
			if (searchParams.get(useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM) === useEventStartFiltersConsts.EVENT_START_FILTER_ALL) {
				// Avoid unnecessary history.replace()
				return;
			}
			searchParams.set(useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM, useEventStartFiltersConsts.EVENT_START_FILTER_ALL);
		}

		history.replace({
			...location,
			search: searchParams.toString(),
		});
	};

	useEffect(() => {
		const filterValue = getFilterValueFromQueryString();
		setCurrentSearchParamsFilterValue(filterValue);

		if (availableFilters) {
			const currentFilter = availableFilters?.find((availableFilter) => availableFilter.value === filterValue);

			if (!currentFilter) {
				updateFilterSearchParam(undefined);
			}

			setCurrentFilterValue(currentFilter?.value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availableFilters, location.search]);

	const updateCurrentFilter = async (item: EventStartFilterResponse | undefined) => updateFilterSearchParam(item?.value);

	const fetchFilteredPopularCategories = () => {
		dispatch(fetchPopularCategories(currentFilterValue));
	};

	// Async only needed for RefreshableContent to work
	const refreshCategories = async () => {
		dispatch(fetchCategories());
		fetchFilteredPopularCategories();
	};

	useEffect(() => {
		if (refreshCategoriesOnFilterChange) {
			void refreshCategories();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshCategoriesOnFilterChange, currentFilterValue]);

	const fetchAvailableFilters = async (categoryId: number) => {
		await saveAvailableFilters(() => CommonAPI.EventFilters.getAvailableFiltersForCategory(categoryId));
	};

	const fetchAvailableEsportFilters = async () => {
		await saveAvailableFilters(() => CommonAPI.EventFilters.getAvailableFiltersForEsport());
	};

	const saveAvailableFilters = async (fetchFunction: () => Promise<AxiosResponse<AvailableFiltersResponse>>) => {
		try {
			const response = await fetchFunction();
			setAvailableFilters(response.data.availableFilters);
		} catch (error: unknown) {
			// If we cannot get the available filters for actual event start dates, provide the player with the default filter set.
			setAvailableFilters(defaultEventStartFilters);
			logger.error(error);
		}
	};

	const fetchFilteredEvents = async (categoryId: number, axiosConfig: AxiosRequestConfig = {}) =>
		SportsApi.getSportDataByCategoryId(categoryId, currentFilterValue, axiosConfig);

	const fetchFilteredEsportEvents = async () => CommonAPI.Livebetting.getAllRunningEsportEvents({ filter: currentFilterValue });

	return {
		currentFilterValue,
		currentSearchParamsFilterValue,
		availableFilters,

		fetchAvailableFilters,
		fetchAvailableEsportFilters,

		fetchFilteredEvents,
		fetchFilteredEsportEvents,
		refreshCategories,

		updateCurrentFilter,
	};
};
